import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { catchError, map, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { failStatus, GenericRequest, successStatus } from '@core/models/request-status.model';
import { Facets, GetProductListResponse, PLPParams, ProductList } from '@b2b/features/product/core/utilities/product-list.model';
import { GetProductResponse, Product } from '@b2b/features/product/core/utilities/product.model';
import { normalizeProduct } from '@b2b/features/product/core/utilities/product.normalizer';
import { Price } from '@b2b/features/product/core/utilities/price.model';
import { GetStockResponse } from '@b2b/features/product/core/utilities/stock.model';
import { GetDeliveryResponse, PostDeliveryRequest } from '@b2b/features/product/core/utilities/delivery.model';

@Injectable({
  providedIn: 'root',
})
export class ProductConnector {
  constructor(
    private http: HttpClient,
    private translateService: TranslateService,
  ) {}

  getProduct(productID: string): Observable<GenericRequest<Product>> {
    const url = `${environment.apiService}/product/${productID}`;
    return this.http.get<GetProductResponse>(url).pipe(
      map(result => {
        return {
          ...successStatus,
          data: normalizeProduct([result])[0],
        };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getProducts(_params: PLPParams): Observable<GenericRequest<ProductList>> {
    let url = _params.query
      ? environment.apiService + `/search?${_params.query}&page=${_params.page ?? 0}&limit=${_params.limit || 24}`
      : environment.apiService + `/search?query=*:score&page=${_params.page ?? 0}&limit=${_params.limit || 24}`;

    if (_params.categoryID) {
      if (!url.includes(`${_params.categoryID}`)) {
        const urlIndex = url.split('&page');
        url = `${urlIndex[0]}:categoria:${_params.categoryID}&page${urlIndex[1]}`;
      }
    }

    return this.http.get<GetProductListResponse>(url).pipe(
      map(data => {
        return {
          facetsGroups: { data: data.facets },
          data: {
            totalProducts: data.totalProducts,
            totalPages: data.totalPages,
            currentPage: data.currentPage,
            products: normalizeProduct(data.products),
            didYouMean: data.didYouMean,
          },
          ...successStatus,
        };
      }),
      catchError(({ error }) => {
        return of({
          ...failStatus,
          message: error,
        });
      }),
    );
  }

  search(ids: string[]): Observable<GenericRequest<Product[]>> {
    const url = `${environment.apiService}/product/search`;
    return this.http.post<GetProductResponse[]>(url, { ids }).pipe(
      map(result => {
        return {
          ...successStatus,
          data: normalizeProduct(result),
        };
      }),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getPrice(productID: string): Observable<GenericRequest<Price>> {
    const url = `${environment.apiService}/pricing/calculate?product=${productID}`;
    return this.http.get<Price>(url).pipe(
      map(result => ({
        ...successStatus,
        data: result,
      })),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getStock(productID: string): Observable<GenericRequest<GetStockResponse>> {
    const url = `${environment.apiService}/stock/calculate?product=${productID}`;

    return this.http.get<GetStockResponse>(url).pipe(
      map(result => ({
        ...successStatus,
        data: result,
      })),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  getDeliveryTime(delivery: PostDeliveryRequest): Observable<GenericRequest<GetDeliveryResponse>> {
    const url = `${environment.apiService}/stock/delivery-time`;

    return this.http.post<GetDeliveryResponse>(url, delivery).pipe(
      map(result => ({
        ...successStatus,
        data: result,
      })),
      catchError(() => {
        return this.translateService.get('generic.requestError').pipe(
          map(message => ({
            ...failStatus,
            message,
          })),
        );
      }),
    );
  }

  normalizeFacts(data: any): Facets[] {
    return data;
  }
}
